import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Slider } from "antd";
import Plot from "react-plotly.js";
import { SEVERITY_COLORS, ALL_SEVERITIES } from "./types";
var MIN_BARS = 20;
/**
 * Pick a "nice" bucket size so the range produces at least MIN_BARS buckets.
 * Starts from the old heuristic and halves until we have enough bars.
 */
function computeBucketSize(fromMs, toMs) {
    var rangeMs = toMs - fromMs;
    if (rangeMs <= 0)
        return 1000;
    // Start with a heuristic bucket size
    var bucket;
    if (rangeMs <= 10 * 60 * 1000)
        bucket = 10 * 1000;
    else if (rangeMs <= 60 * 60 * 1000)
        bucket = 60 * 1000;
    else if (rangeMs <= 6 * 60 * 60 * 1000)
        bucket = 5 * 60 * 1000;
    else if (rangeMs <= 24 * 60 * 60 * 1000)
        bucket = 30 * 60 * 1000;
    else if (rangeMs <= 7 * 24 * 60 * 60 * 1000)
        bucket = 60 * 60 * 1000;
    else
        bucket = 6 * 60 * 60 * 1000;
    // Shrink bucket size until we get at least MIN_BARS buckets
    while (Math.floor(rangeMs / bucket) < MIN_BARS && bucket > 1000) {
        bucket = Math.max(Math.floor(bucket / 2), 1000);
    }
    return bucket;
}
function formatSliderLabel(ms) {
    var d = new Date(ms);
    var pad = function (n) { return String(n).padStart(2, "0"); };
    var month = pad(d.getMonth() + 1);
    var day = pad(d.getDate());
    var hours = pad(d.getHours());
    var mins = pad(d.getMinutes());
    return "".concat(month, "/").concat(day, " ").concat(hours, ":").concat(mins);
}
var LogTimeline = function (_a) {
    var logs = _a.logs, from = _a.from, to = _a.to, hasMore = _a.hasMore, oldestFetchedTimestamp = _a.oldestFetchedTimestamp, onViewRangeCommit = _a.onViewRangeCommit;
    // Slider view range – defaults to full range, resets when from/to change
    var _b = useState([from, to]), viewRange = _b[0], setViewRange = _b[1];
    // Track from/to with a ref so the useEffect only resets when they truly change
    var prevRangeRef = useRef([from, to]);
    useEffect(function () {
        if (prevRangeRef.current[0] !== from || prevRangeRef.current[1] !== to) {
            prevRangeRef.current = [from, to];
            setViewRange([from, to]);
        }
    }, [from, to]);
    var handleSliderChange = useCallback(function (value) {
        setViewRange([value[0], value[1]]);
    }, []);
    // When the user releases the slider, commit the narrowed range to the parent
    var handleSliderCommit = useCallback(function (value) {
        var newFrom = value[0];
        var newTo = value[1];
        // Only commit if the range was actually narrowed
        if (newFrom !== from || newTo !== to) {
            onViewRangeCommit === null || onViewRangeCommit === void 0 ? void 0 : onViewRangeCommit([newFrom, newTo]);
        }
    }, [from, to, onViewRangeCommit]);
    var viewFrom = viewRange[0];
    var viewTo = viewRange[1];
    var traces = useMemo(function () {
        if (logs.length === 0)
            return [];
        var bucketSize = computeBucketSize(viewFrom, viewTo);
        // Build severity-grouped bucket maps (only logs within the view range)
        var bucketMap = {};
        ALL_SEVERITIES.forEach(function (sev) {
            bucketMap[sev] = {};
        });
        var errorPattern = /(SERVICE_ERROR|HTTP_ERROR|VALIDATION_ERROR)/;
        logs.forEach(function (entry) {
            var ts = new Date(entry.createdAt).getTime();
            if (ts < viewFrom || ts > viewTo)
                return; // cull outside view range
            var bucketStart = Math.floor(ts / bucketSize) * bucketSize;
            var bucketKey = new Date(bucketStart).toISOString();
            var severity = errorPattern.test(entry.message || "")
                ? "ERROR"
                : ALL_SEVERITIES.includes((entry.level || "INFO").toUpperCase())
                    ? (entry.level || "INFO").toUpperCase()
                    : "INFO";
            if (!bucketMap[severity][bucketKey]) {
                bucketMap[severity][bucketKey] = 0;
            }
            bucketMap[severity][bucketKey]++;
        });
        // Generate all bucket keys spanning the full view range so we always have at least MIN_BARS
        var firstBucket = Math.floor(viewFrom / bucketSize) * bucketSize;
        var allBuckets = [];
        for (var t = firstBucket; t <= viewTo; t += bucketSize) {
            allBuckets.push(new Date(t).toISOString());
        }
        // Build traces per severity
        return ALL_SEVERITIES.map(function (severity) { return ({
            x: allBuckets,
            y: allBuckets.map(function (b) { return bucketMap[severity][b] || 0; }),
            type: "bar",
            name: severity,
            marker: { color: SEVERITY_COLORS[severity] },
            hovertemplate: "%{x}<br>".concat(severity, ": %{y}<extra></extra>"),
        }); }).filter(function (trace) { return trace.y.some(function (v) { return v > 0; }); });
    }, [logs, viewFrom, viewTo]);
    // Determine whether there's an unfetched region to visualize
    var showUnfetchedRegion = hasMore && oldestFetchedTimestamp != null && oldestFetchedTimestamp > viewFrom;
    // Build Plotly shapes + annotations for the unfetched region
    var unfetchedShapes = useMemo(function () {
        if (!showUnfetchedRegion)
            return [];
        return [
            {
                type: "rect",
                xref: "x",
                yref: "paper",
                x0: new Date(viewFrom).toISOString(),
                x1: new Date(oldestFetchedTimestamp).toISOString(),
                y0: 0,
                y1: 1,
                fillcolor: "rgba(0,0,0,0.06)",
                line: { width: 0 },
                layer: "above",
            },
        ];
    }, [showUnfetchedRegion, viewFrom, oldestFetchedTimestamp]);
    var unfetchedAnnotations = useMemo(function () {
        if (!showUnfetchedRegion)
            return [];
        // Place text in the center of the unfetched region
        var midpoint = (viewFrom + oldestFetchedTimestamp) / 2;
        return [
            {
                x: new Date(midpoint).toISOString(),
                y: 0.5,
                xref: "x",
                yref: "paper",
                text: "More logs may exist. Scroll down to populate…",
                showarrow: false,
                font: { size: 10, color: "#888" },
                bgcolor: "rgba(255,255,255,0.7)",
                borderpad: 3,
            },
        ];
    }, [showUnfetchedRegion, viewFrom, oldestFetchedTimestamp]);
    if (logs.length === 0) {
        return null;
    }
    return (_jsx("div", { className: "card", style: { marginBottom: 0 }, children: _jsxs("div", { className: "card-body", style: { padding: "5px 10px" }, children: [_jsx(Plot, { data: traces, layout: {
                        barmode: "stack",
                        height: 140,
                        margin: { t: 5, b: 30, l: 40, r: 10 },
                        xaxis: {
                            type: "date",
                            range: [new Date(viewFrom).toISOString(), new Date(viewTo).toISOString()],
                            fixedrange: true,
                        },
                        yaxis: {
                            fixedrange: true,
                            title: { text: "" },
                            dtick: undefined,
                        },
                        showlegend: true,
                        legend: {
                            orientation: "h",
                            y: 1.15,
                            x: 0,
                            font: { size: 11 },
                        },
                        paper_bgcolor: "transparent",
                        plot_bgcolor: "transparent",
                        bargap: 0.1,
                        shapes: unfetchedShapes,
                        annotations: unfetchedAnnotations,
                    }, config: {
                        displayModeBar: false,
                        responsive: true,
                    }, useResizeHandler: true, style: { width: "100%", height: "140px" } }), _jsx("div", { style: { padding: "0 10px 2px", display: "flex", alignItems: "center", gap: 8 }, children: _jsx(Slider, { range: true, min: from, max: to, step: Math.max(Math.floor((to - from) / 500), 1000), value: [viewFrom, viewTo], onChange: handleSliderChange, onChangeComplete: handleSliderCommit, tooltip: {
                            formatter: function (val) { return (val != null ? formatSliderLabel(val) : ""); },
                        }, style: { flex: 1, margin: "0 4px" } }) })] }) }));
};
export default LogTimeline;
